import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";

const normalizeBrandRecord = (record) => ({
  ...record,
  name: record?.name ?? record?.brandName ?? "",
  brandName: record?.brandName ?? record?.name ?? "",
});

const normalizeProductTypeRecord = (record) => ({
  ...record,
  name: record?.name ?? record?.specificType ?? "",
  specificType: record?.specificType ?? record?.name ?? "",
  slug: record?.slug ?? "",
  coverImageKey: record?.coverImageKey ?? null,
  coverImageThumbnailKey: record?.coverImageThumbnailKey ?? null,
  coverImageCardKey: record?.coverImageCardKey ?? null,
  coverImageFullKey: record?.coverImageFullKey ?? null,
  coverImageUrl: record?.coverImageUrl ?? null,
  isPublished: Boolean(record?.isPublished ?? record?.publishedInStorefront),
  publishedInStorefront: Boolean(
    record?.publishedInStorefront ?? record?.isPublished
  ),
  sortOrder: Number(record?.sortOrder ?? 0),
});

const normalizeColorRecord = (record) => ({
  ...record,
  name: record?.name ?? record?.colorName ?? "",
  colorName: record?.colorName ?? record?.name ?? "",
  hexCode: record?.hexCode ?? null,
});

// helper for delete invalidation (endpoint -> table key)
const endpointToTableKey = (endpoint) => {
  if (!endpoint) return undefined;
  const e = String(endpoint).toLowerCase();
  if (e.includes("brand")) return "brands";
  if (e.includes("color")) return "colors";
  if (e.includes("number")) return "numbers";
  if (e.includes("type")) return "types";
  if (e.includes("location")) return "locations";
  return undefined;
};

export const cmsPanelsApi = createApi({
  reducerPath: "cmsPanelsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, { acceptJson: true }),
  }),
  tagTypes: ["Table", "Brand", "Color", "Number", "Type", "Location"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,

  endpoints: (builder) => ({
    // Generic table for buttons: 'brands'|'colors'|'numbers'|'types'|'locations'
    fetchPanelTable: builder.query({
      query: (button) => `panels/${button}`,
      transformResponse: (res, _meta, button) => {
        const rows = res?.success ? res.data : res ?? [];
        if (!Array.isArray(rows)) {
          return [];
        }

        if (rows.length && rows[0]?.colorName !== undefined) {
          return rows.map(normalizeColorRecord);
        }

        if (
          button === "types" ||
          (rows.length && rows[0]?.specificType !== undefined)
        ) {
          return rows.map(normalizeProductTypeRecord);
        }

        return rows;
      },
      providesTags: (_res, _err, button) => [{ type: "Table", id: button }],
    }),

    // Convenience fetchers (uniform data shape)
    fetchBrands: builder.query({
      query: (type) => ({ url: "panels/brands", params: { type } }),
      transformResponse: (res) =>
        ((res?.success ? res.data : res ?? []) || []).map(normalizeBrandRecord),
      providesTags: ["Brand", { type: "Table", id: "brands" }],
    }),
    fetchColors: builder.query({
      query: () => ({ url: "panels/colors" }),
      transformResponse: (res) =>
        ((res?.success ? res.data : res ?? []) || []).map(normalizeColorRecord),
      providesTags: ["Color", { type: "Table", id: "colors" }],
    }),
    fetchNumbers: builder.query({
      query: (type) => ({ url: "panels/numbers", params: { type } }),
      transformResponse: (res) => (res?.success ? res.data : res ?? []),
      providesTags: ["Number", { type: "Table", id: "numbers" }],
    }),
    fetchTypes: builder.query({
      query: (type) => ({ url: "panels/types", params: { type } }),
      transformResponse: (res) =>
        ((res?.success ? res.data : res ?? []) || []).map(
          normalizeProductTypeRecord
        ),
      providesTags: ["Type", { type: "Table", id: "types" }],
    }),

    // Locations (replaces old apiCalls.js helper)
    fetchLocations: builder.query({
      query: () => "panels/locations",
      transformResponse: (res) => res?.data ?? res ?? [],
      providesTags: ["Location", { type: "Table", id: "locations" }],
    }),

    // CREATE
    saveColorData: builder.mutation({
      query: (body) => ({ url: "panels/color", method: "POST", body }),
      invalidatesTags: ["Color", { type: "Table", id: "colors" }],
    }),
    saveBrandData: builder.mutation({
      query: (body) => ({ url: "panels/brand", method: "POST", body }),
      invalidatesTags: ["Brand", { type: "Table", id: "brands" }],
    }),
    saveNumberData: builder.mutation({
      query: (body) => ({ url: "panels/number", method: "POST", body }),
      invalidatesTags: ["Number", { type: "Table", id: "numbers" }],
    }),
    saveSpecificTypeData: builder.mutation({
      query: (body) => ({ url: "panels/type", method: "POST", body }),
      invalidatesTags: ["Type", { type: "Table", id: "types" }],
    }),
    uploadTypeCoverImage: builder.mutation({
      query: (file) => {
        const body = new FormData();
        body.append("file", file);
        return {
          url: "panels/type/cover-image",
          method: "POST",
          body,
        };
      },
    }),
    saveLocationsData: builder.mutation({
      query: (body) => ({ url: "panels/location", method: "POST", body }),
      invalidatesTags: ["Location", { type: "Table", id: "locations" }],
    }),

    // EDIT
    editColorData: builder.mutation({
      query: (body) => ({ url: "panels/color", method: "PUT", body }),
      invalidatesTags: ["Color", { type: "Table", id: "colors" }],
    }),
    editBrandData: builder.mutation({
      query: (body) => ({ url: "panels/brand", method: "PUT", body }),
      invalidatesTags: ["Brand", { type: "Table", id: "brands" }],
    }),
    editNumberData: builder.mutation({
      query: (body) => ({ url: "panels/number", method: "PUT", body }),
      invalidatesTags: ["Number", { type: "Table", id: "numbers" }],
    }),
    editTypeData: builder.mutation({
      query: (body) => ({ url: "panels/type", method: "PUT", body }),
      invalidatesTags: ["Type", { type: "Table", id: "types" }],
    }),
    editLocationData: builder.mutation({
      query: (body) => ({ url: "panels/location", method: "PUT", body }),
      invalidatesTags: ["Location", { type: "Table", id: "locations" }],
    }),

    // DELETE generic
    deleteItem: builder.mutation({
      query: ({ endpoint, ...body }) => ({
        url: `panels/${endpoint}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_r, _e, arg) => {
        const key = endpointToTableKey(arg?.endpoint) || "brands";
        return [
          { type: "Table", id: key },
          "Brand",
          "Color",
          "Number",
          "Type",
          "Location",
        ];
      },
    }),
  }),
});

export const {
  useFetchPanelTableQuery,
  useLazyFetchPanelTableQuery,
  useFetchBrandsQuery,
  useFetchColorsQuery,
  useFetchNumbersQuery,
  useFetchTypesQuery,
  useFetchLocationsQuery,
  useSaveColorDataMutation,
  useSaveBrandDataMutation,
  useSaveNumberDataMutation,
  useSaveSpecificTypeDataMutation,
  useUploadTypeCoverImageMutation,
  useSaveLocationsDataMutation,
  useEditColorDataMutation,
  useEditBrandDataMutation,
  useEditNumberDataMutation,
  useEditTypeDataMutation,
  useEditLocationDataMutation,
  useDeleteItemMutation,
} = cmsPanelsApi;
